import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import StepVerticalTrackBar from 'components/stepper/vertical-track-bar';
import { cn } from 'lib/utils';

interface CreateApiSidebarProps {
  toolId: string;
  isBlocksTool: boolean;
  className?: string;
}

export function CreateApiSidebar({ toolId, isBlocksTool, className }: Readonly<CreateApiSidebarProps>) {
  const { t } = useTranslation();
  const backUrl = `/tool-details/${encodeURIComponent(toolId)}?tab=api${isBlocksTool ? '&isBlocksTool=true' : ''}`;
  const createApiLabel = t('CREATE_API') || 'Create API';
  const createApiDescription =
    t('CREATE_API_DESCRIPTION') ||
    'Set up your API details, parameters, and debug to validate responses.';

  return (
    <aside
      className={cn(
        'shrink-0 border-border bg-background',
        'w-full border-b px-4 pb-5 pt-4 sm:px-6',
        'lg:w-72 lg:border-b-0 lg:border-r lg:px-5 lg:pb-5 lg:pt-24',
        'xl:w-80',
        className
      )}
    >
      <div className="mx-auto w-full max-w-xl lg:max-w-none">
        <div className="flex min-h-12 items-center justify-center lg:hidden">
          <Link
            to={backUrl}
            className="inline-flex items-center gap-2 text-foreground transition-opacity hover:opacity-70"
            aria-label={t('CLOSE_CREATE_API') || 'Close create API'}
          >
            <X className="h-6 w-6 shrink-0" strokeWidth={1.5} />
            <span className="text-lg font-semibold leading-none">{createApiLabel}</span>
          </Link>
        </div>

        <div className="hidden lg:flex lg:items-start lg:gap-2">
          <Link
            to={backUrl}
            className="mt-0.5 shrink-0 text-foreground transition-opacity hover:opacity-70"
            aria-label={t('CLOSE_CREATE_API') || 'Close create API'}
          >
            <X className="h-8 w-8" strokeWidth={1} />
          </Link>
          <div className="min-w-0">
            <p className="text-lg font-semibold leading-tight">{createApiLabel}</p>
            <p className="mt-2 text-sm text-muted-foreground">{createApiDescription}</p>
          </div>
        </div>

        <p className="text-center text-sm text-muted-foreground lg:hidden">{createApiDescription}</p>

        <div className="mt-5 lg:mt-8">
          <StepVerticalTrackBar orientation="horizontal" className="mx-auto max-w-sm lg:hidden" />
          <StepVerticalTrackBar orientation="vertical" className="hidden lg:block" />
        </div>
      </div>
    </aside>
  );
}
